import type {
  AlertsTypeFilter,
  AlertsWhenFilter,
} from "../../lib/routerSearch";
import {
  Button,
  DateRangePicker,
  Inline,
  Select,
  type DateRange,
} from "../../ui";

export interface AlertHistoryFilterValues {
  created_after?: string;
  created_before?: string;
  type?: AlertsTypeFilter;
  when?: AlertsWhenFilter;
}

export interface AlertHistoryFiltersProps {
  onChange: (next: AlertHistoryFilterValues) => void;
  value: AlertHistoryFilterValues;
}

function startOfDayIso(date: Date): string {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).toISOString();
}

function nextDayIso(date: Date): string {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + 1,
  ).toISOString();
}

function inclusiveEndDate(beforeIso: string): Date {
  return new Date(new Date(beforeIso).getTime() - 1);
}

function createdRange(value: AlertHistoryFilterValues): DateRange {
  return {
    end: value.created_before ? inclusiveEndDate(value.created_before) : null,
    start: value.created_after ? new Date(value.created_after) : null,
  };
}

function hasActiveFilters(value: AlertHistoryFilterValues): boolean {
  return Boolean(
    value.created_after || value.created_before || value.type || value.when,
  );
}

export function AlertHistoryFilters({
  onChange,
  value,
}: AlertHistoryFiltersProps) {
  return (
    <Inline gap={3}>
      <Select
        label="When"
        size="sm"
        value={value.when ?? ""}
        onChange={(event) => {
          const when = event.currentTarget.value;
          onChange({
            ...value,
            when: when === "firing" || when === "resolved" ? when : undefined,
          });
        }}
      >
        <option value="">Any transition</option>
        <option value="firing">Firing</option>
        <option value="resolved">Resolved</option>
      </Select>
      <Select
        label="Detection type"
        size="sm"
        value={value.type ?? ""}
        onChange={(event) => {
          const type = event.currentTarget.value;
          onChange({
            ...value,
            type: type === "event" || type === "threshold" ? type : undefined,
          });
        }}
      >
        <option value="">Any type</option>
        <option value="threshold">Threshold</option>
        <option value="event">Event</option>
      </Select>
      <DateRangePicker
        label="Created"
        placeholder="Any time"
        value={createdRange(value)}
        onChange={(range) => {
          onChange({
            ...value,
            created_after: range.start ? startOfDayIso(range.start) : undefined,
            created_before: range.end ? nextDayIso(range.end) : undefined,
          });
        }}
      />
      {hasActiveFilters(value) ? (
        <Button
          onClick={() => {
            onChange({});
          }}
          size="sm"
          type="button"
          variant="secondary"
        >
          Clear filters
        </Button>
      ) : null}
    </Inline>
  );
}
